/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit } from '@angular/core';
import {
  AdminWhatsappSendReq,
  GallaboxTemplatesResponse,
  UserProfileService,
} from '../../../services/user-profile.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import {
  EmailAuditLogEntry,
  UserJourneyResp,
  UserLyfisScore,
  UserTable,
} from '../../../models/user-profile/UserProfileSearchResponse';
import * as moment from 'moment';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import { SessionsService } from 'projects/app-core/src/app/session/service/sessions.service';
import { SessionGridStatsData } from 'projects/app-core/src/app/session/models/sessionStatsData';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NzDrawerPlacement } from 'ng-zorro-antd/drawer';
import {
  AnyoWhatsAppTemplateURLParams,
  PlaceHolders,
} from '../../../models/AnyoWhatsappTemplate';
import { UserTags } from 'projects/app-core/src/app/posts/model/CirclePost';
import {
  NetworkUtilsService,
  URL_DICT,
} from 'projects/app-core/src/service/network-utils.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { TherapyVerticals } from 'projects/app-core/src/models/TherapyVerticals';
import { TherapyConcerns } from 'projects/app-core/src/models/TherapyConcerns';
import {
  PolicyBookingTrace,
  PolicyEvaluateWinningRule,
  UserSessions,
} from '../../../models/user-profile/UserProfileSearchResponse';
import { TherapistService } from '../../../services/therapist.service';
import { IAnyoTherapist } from '../../../models/IAnyoTherapist';
import { TherapistExpertLevel } from 'projects/app-core/src/models/TherapistExpertLevel';
import {
  PolicyRule,
  PolicyService,
  UserPolicyRulePayload,
} from '../../partner/policy-controls/policy.service';
import { ExpertChatRow } from '../../../services/user-profile.service';

type PanelData = any;

interface DropdownOptions {
  value: string;
  data: string;
}

// ── Personal (per-user) policy rule override — reuses the same FOR/WHEN/THEN card pattern
// already duplicated in policy-controls.component.ts (company) and global-policy-rules.component.ts
// (global). Session Booking outputs here deliberately match the smaller field set the ticket
// documented for this reused form (payer, duration, allowed, cooldown, blockReason) — NOT the
// newer self-pay-pricing extension present on the company page. See notes/anyo-portal-update.md.

interface PersonalConditionRow {
  fact: string;
  operator: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  params?: Record<string, unknown>;
}

interface PersonalRuleUI {
  id?: string;
  name: string;
  priority: number;
  active: boolean;
  action: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[] };
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: PersonalConditionRow[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outputs: Record<string, any>;
  expanded: boolean;
  saving: boolean;
}

interface ExpertChatRowUI extends ExpertChatRow {
  saving: boolean;
}

const PERSONAL_RULE_ACTIONS = ['Session Booking', 'Event Registration', 'Diagnostic Order'];

const PERSONAL_RULE_FACTS: Record<string, { value: string; label: string; type: 'number' | 'bool'; hasParams?: boolean }[]> = {
  'Session Booking': [
    { value: 'sessionsInLastNDays', label: 'Sessions booked in last N days (this vertical + concern)', type: 'number', hasParams: true },
    { value: 'totalSessionsInLastNDays', label: 'Sessions booked in last N days (all verticals)', type: 'number', hasParams: true },
    { value: 'sessionsConsumedInPeriod', label: 'Sessions booked (this concern)', type: 'number' },
    { value: 'totalSessionsConsumedInPeriod', label: 'Sessions booked (all verticals)', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
    { value: 'hasHadSessionWithTherapist', label: 'Has had session with this therapist', type: 'bool' },
    { value: 'sessionsWithThisTherapist', label: 'Session count with this expert', type: 'number' },
  ],
  'Event Registration': [
    { value: 'eventsConsumedInPeriod', label: 'Events registered this period', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
  'Diagnostic Order': [
    { value: 'diagnosticsOrdersConsumedInPeriod', label: 'Diagnostic orders this period', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
};

// Full parity with the company/global rule forms — same fields, including self-pay pricing
// (only relevant/visible when payer='user'; see isPersonalFieldVisible).
const PERSONAL_RULE_OUTPUTS: Record<
  string,
  { key: string; label: string; type: 'select' | 'number' | 'text'; options?: { v: string; l: string }[] }[]
> = {
  'Session Booking': [
    { key: 'payer', label: 'Who pays', type: 'select', options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }] },
    { key: 'sessionMinutes', label: 'Session duration', type: 'select', options: [{ v: '20', l: '20 min' }, { v: '30', l: '30 min' }, { v: '45', l: '45 min' }, { v: '60', l: '60 min' }, { v: '90', l: '90 min' }] },
    { key: 'sessionAllowed', label: 'Allow booking', type: 'select', options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }] },
    { key: 'cooldown', label: 'Min. days between sessions', type: 'number' },
    { key: 'blockReason', label: 'Block reason (shown to user)', type: 'text' },
    // Self-pay pricing — only shown when payer='user'. GST and markup RATE come from
    // Contract Settings (partner-level); this rule only picks the pricing MODE and,
    // for slab mode, the markup VALUE on top of it.
    {
      key: 'selfPayPricingMode', label: 'Self-pay pricing', type: 'select',
      options: [
        { v: 'slab', l: 'Therapist slab pricing + markup' },
        { v: 'flat', l: 'Flat amount' },
        { v: 'flatSlab', l: 'Flat amount by session duration' },
      ],
    },
    {
      key: 'selfPaySlabSource', label: 'Slab pricing source', type: 'select',
      options: [{ v: 'corporate', l: 'Corporate slab' }, { v: 'retail', l: 'Retail slab' }],
    },
    {
      key: 'selfPayMarkupType', label: 'Markup type', type: 'select',
      options: [{ v: 'percent', l: 'Percent (%)' }, { v: 'fixed', l: 'Fixed amount (₹)' }],
    },
    { key: 'selfPayMarkupValue', label: 'Markup value', type: 'number' },
    { key: 'selfPayFlatAmount', label: 'Flat amount (₹)', type: 'number' },
  ],
  'Event Registration': [
    { key: 'payer', label: 'Who pays', type: 'select', options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }] },
    { key: 'eventAllowed', label: 'Allow registration', type: 'select', options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }] },
  ],
  'Diagnostic Order': [
    { key: 'payer', label: 'Who pays', type: 'select', options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }] },
    { key: 'diagAllowed', label: 'Allow ordering', type: 'select', options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }] },
  ],
};

// Maps each action to the output key that controls whether the action is allowed.
const PERSONAL_ACTION_ALLOWED_FIELD: Record<string, string> = {
  'Session Booking': 'sessionAllowed',
  'Event Registration': 'eventAllowed',
  'Diagnostic Order': 'diagAllowed',
};

// Output keys that must be cleared/disabled when the action is blocked. blockReason is
// intentionally excluded — it is still meaningful when blocked.
const PERSONAL_BLOCKED_CLEARS: Record<string, string[]> = {
  'Session Booking': ['payer', 'sessionMinutes', 'cooldown'],
  'Event Registration': ['payer'],
  'Diagnostic Order': ['payer'],
};

// Self-pay pricing fields — only relevant on a Session Booking rule whose payer='user'.
const PERSONAL_SELF_PAY_SLAB_ONLY_FIELDS = ['selfPaySlabSource', 'selfPayMarkupType', 'selfPayMarkupValue'];
const PERSONAL_SELF_PAY_FLAT_ONLY_FIELDS = ['selfPayFlatAmount'];
const PERSONAL_SELF_PAY_PRICING_FIELDS = ['selfPayPricingMode', ...PERSONAL_SELF_PAY_SLAB_ONLY_FIELDS, ...PERSONAL_SELF_PAY_FLAT_ONLY_FIELDS];

const PERSONAL_RULE_NUM_OPS = [
  { value: 'equal', label: '=' },
  { value: 'notEqual', label: '≠' },
  { value: 'lessThan', label: '<' },
  { value: 'greaterThan', label: '>' },
  { value: 'lessThanInclusive', label: '≤' },
  { value: 'greaterThanInclusive', label: '≥' },
];

function parsePersonalConditions(raw: unknown): {
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: PersonalConditionRow[];
} {
  const obj = raw as Record<string, unknown> | null | undefined;
  if (!obj) return { conditionMode: 'always', condLogic: 'all', condRows: [] };
  const allRows = (obj['all'] as unknown[]) ?? [];
  const anyRows = (obj['any'] as unknown[]) ?? [];
  const condLogic: 'all' | 'any' = anyRows.length > 0 && allRows.length === 0 ? 'any' : 'all';
  const rows = condLogic === 'any' ? anyRows : allRows;
  if (!rows.length) return { conditionMode: 'always', condLogic, condRows: [] };
  return {
    conditionMode: 'when',
    condLogic,
    condRows: rows.map((r) => {
      const row = r as Record<string, unknown>;
      return {
        fact: String(row['fact'] ?? ''),
        operator: String(row['operator'] ?? 'equal'),
        value: (row['value'] ?? '') as string | number | boolean,
        ...(row['params'] ? { params: row['params'] as Record<string, unknown> } : {}),
      };
    }),
  };
}

function buildPersonalConditions(
  conditionMode: 'always' | 'when',
  condLogic: 'all' | 'any',
  condRows: PersonalConditionRow[],
): unknown {
  if (conditionMode === 'always' || !condRows.length) return { all: [] };
  const validRows = condRows.filter((r) => r.fact && r.fact.trim() !== '');
  if (!validRows.length) return { all: [] };
  return {
    [condLogic]: validRows.map((r) => ({
      fact: r.fact,
      operator: r.operator,
      value: r.value,
      ...(r.params && Object.keys(r.params).length > 0 ? { params: r.params } : {}),
    })),
  };
}

function toPersonalRuleUI(rule: PolicyRule): PersonalRuleUI {
  const { conditionMode, condLogic, condRows } = parsePersonalConditions(rule.conditions);
  const scope = rule.scope || {};
  return {
    id: rule.id,
    name: rule.name,
    priority: rule.priority,
    active: rule.active,
    action: rule.action || 'Session Booking',
    scope: {
      ...scope,
      verticalId: Array.isArray(scope.verticalId) ? scope.verticalId : scope.verticalId ? [scope.verticalId as unknown as string] : undefined,
      concernId: Array.isArray(scope.concernId) ? scope.concernId : scope.concernId ? [scope.concernId as unknown as string] : undefined,
      therapistIds: Array.isArray(scope.therapistIds) ? scope.therapistIds : scope.therapistIds ? [scope.therapistIds as unknown as string] : undefined,
    },
    conditionMode,
    condLogic,
    condRows,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    outputs: (rule.outputs as Record<string, any>) || {},
    expanded: false,
    saving: false,
  };
}

function fromPersonalRuleUI(ui: PersonalRuleUI, userId: string, companyId: string): UserPolicyRulePayload {
  const outputs = Object.fromEntries(
    Object.entries(ui.outputs).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  );
  return {
    name: ui.name,
    priority: ui.priority,
    active: ui.active,
    action: ui.action,
    companyId,
    userId,
    scope: ui.scope,
    conditions: buildPersonalConditions(ui.conditionMode, ui.condLogic, ui.condRows),
    outputs,
  };
}

@Component({
  selector: 'app-search-user',
  templateUrl: './search-user.component.html',
  styleUrl: './search-user.component.scss',
})
export class SearchUserComponent implements OnInit {
  constructor(
    private userProfileService: UserProfileService,
    private toastrService: ToastService,
    public utils: UtilsService,
    private sessionService: SessionsService,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private api: NetworkUtilsService,
    private router: Router,
    private masterdataService: MasterdataService,
    private therapistService: TherapistService,
    private policyService: PolicyService,
  ) {}

  // ── Policy Debug section ──
  verticals: TherapyVerticals[] = [];
  allConcerns: TherapyConcerns[] = [];
  filteredConcerns: TherapyConcerns[] = [];
  therapists: IAnyoTherapist[] = [];
  debugVerticalId = '';
  debugConcernId = '';
  debugTherapistId = '';
  debugLoading = false;
  debugResult: {
    user: {
      userId: string;
      email: string;
      userType: string;
      partnerId?: string;
    };
    company: {
      companyId: string;
      companyName: string;
      subscriptionStart?: string;
      subscriptionEnd?: string;
    };
    facts: Record<string, unknown>;
    rules: {
      ruleId: string;
      ruleName: string;
      priority: number;
      isGlobal: boolean;
      matched: boolean;
      outputs?: Record<string, unknown>;
      conditions?: unknown;
      scope?: unknown;
    }[];
    finalOutputs: PolicyBookingTrace['outputs'];
    winnersByField: Record<string, PolicyEvaluateWinningRule | null>;
  } | null = null;

  // ── Personal Policy Rules (APH-5224) ──
  expertLevels: TherapistExpertLevel[] = [];
  personalRulesUI: PersonalRuleUI[] = [];
  personalRulesLoading = false;
  readonly PERSONAL_RULE_ACTIONS = PERSONAL_RULE_ACTIONS;
  readonly PERSONAL_RULE_FACTS = PERSONAL_RULE_FACTS;
  readonly PERSONAL_RULE_OUTPUTS = PERSONAL_RULE_OUTPUTS;
  readonly PERSONAL_RULE_NUM_OPS = PERSONAL_RULE_NUM_OPS;

  get canAddPersonalRule(): boolean {
    return !!this.userProfileResp?.userCardDetails?.companyId;
  }

  // ── Company + Global rules (APH-5224) — read-only context view alongside the personal
  // rules above, so an admin sees everything that can affect this one employee in one place.
  // Created/edited only on their own existing pages (Policy Controls / Global Rules).
  companyRulesUI: PersonalRuleUI[] = [];
  companyRulesLoading = false;
  globalRulesUI: PersonalRuleUI[] = [];
  globalRulesLoading = false;

  // ── DM Control table (APH-5224) ──
  expertChats: ExpertChatRowUI[] = [];
  expertChatsLoading = false;

  // ── Rule detail modal ──
  ruleDetailVisible = false;
  selectedRule: {
    ruleId: string;
    ruleName: string;
    priority: number;
    isGlobal: boolean;
    matched: boolean;
    outputs?: Record<string, unknown>;
    conditions?: unknown;
    scope?: unknown;
  } | null = null;

  openRuleDetail(rule: typeof this.selectedRule) {
    this.selectedRule = rule;
    this.ruleDetailVisible = true;
  }

  closeRuleDetail() {
    this.ruleDetailVisible = false;
    this.selectedRule = null;
  }

  // ── Session policy trace drawer ──
  policyTraceDrawerVisible = false;
  selectedSessionTrace: PolicyBookingTrace | null = null;
  userTags = UserTags;
  email: string = '';
  spinner: boolean = false;
  userProfileResp!: UserJourneyResp;
  searched: boolean = false;
  panels: {
    active: boolean;
    name: string;
    totalCount: number;
    data: PanelData[];
  }[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  paginatedData: {
    active: boolean;
    name: string;
    totalCount: number;
    data: PanelData[];
  }[] = [];

  visibleWhatsapp = false;
  placement: NzDrawerPlacement = 'right';
  selectedWhatsappTemplate: string = '';
  whatsappGallaboxTemplates: GallaboxTemplatesResponse[] = [];
  selectedGallaboxWhatsappTemplate!: GallaboxTemplatesResponse;
  whatsappTemplates: DropdownOptions[] = [];
  placeholders: { key: PlaceHolders; value: string }[] = [];
  urlParams: { key: AnyoWhatsAppTemplateURLParams; value: string }[] = [];
  isSessionBookingUnBlockModalOpen: boolean = false;
  isSessionBookingBlockModalOpen: boolean = false;
  blockUserTillDate: Date | null = null;
  isFixMemberPassModalOpen: boolean = false;
  isFixingMemberPass: boolean = false;
  whitelistedDomains: string[] = [];

  promptText = '';
  loading = false;

  // extractPlaceholders(input: string): void {
  //   if (!input) {
  //     this.placeholders = [];
  //     return;
  //   }

  //   const matches = input.match(/{{(.*?)}}/g) || [];
  //   const keys = Array.from(
  //     new Set(matches.map((m) => m.replace(/{{|}}/g, ''))),
  //   );

  //   // Preserve existing values
  //   const existingMap = new Map(this.placeholders.map((p) => [p.key, p.value]));

  //   // Build updated placeholder array
  //   this.placeholders = keys.map((key) => ({
  //     key,
  //     value: existingMap.get(key) || '',
  //   }));
  // }

  // onTextareaChange(value: string) {
  //   this.promptText = value;
  //   this.extractPlaceholders(value);
  // }

  onSearchUserChange($event: string) {
    this.selectedGallaboxWhatsappTemplate = this.whatsappGallaboxTemplates.find(
      (data) => data.name === $event,
    )!;
    console.log(this.selectedGallaboxWhatsappTemplate);
    this.placeholders = this.selectedGallaboxWhatsappTemplate.variables.map(
      (data) => {
        return {
          key: data,
          value: '',
        };
      },
    );
    this.urlParams = this.selectedGallaboxWhatsappTemplate.urlParams.map(
      (data) => ({
        key: data,
        value: '',
      }),
    );
  }

  sendWhatsappMessage() {
    this.loading = true;
    const req: AdminWhatsappSendReq = {
      userId: this.userProfileResp.userCardDetails.id,
      templateId: this.selectedGallaboxWhatsappTemplate.anyoTemplateId,
    };

    const isNotEmptyValues = this.placeholders.every(
      (data) => data.value !== '',
    );
    if (this.placeholders.length > 0 && isNotEmptyValues) {
      const placeHolderDataObj: Record<string, string> = {};
      this.placeholders.forEach((data) => {
        placeHolderDataObj[data.key] = data.value;
      });
      req.placeHolderData = placeHolderDataObj;
    }
    const isNotEmptyParamsValues = this.urlParams.every(
      (data) => data.value !== '',
    );
    if (this.urlParams.length > 0 && isNotEmptyParamsValues) {
      const urlParamsObj: Record<string, string> = {};
      this.urlParams.forEach((data) => {
        urlParamsObj[data.key] = data.value;
      });
      req.urlParams = urlParamsObj;
    }
    this.userProfileService.sendWhatsappMessage(req).subscribe({
      next: () => {
        this.toastrService.showSuccess('Successfully sent!!');
        this.loading = false;
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(errorBody.description);
        this.loading = false;
      },
    });
  }

  openWhatsapp(): void {
    this.visibleWhatsapp = true;
    this.getWhatsappTemplates();
  }

  closeWhatsapp(): void {
    this.visibleWhatsapp = false;
  }

  generatePanels(userTableDetails: UserTable) {
    this.panels = Object.keys(userTableDetails).map((key) => {
      const data = userTableDetails[key as keyof UserTable];
      let isArray = data ? (Array.isArray(data) ? data : [data]) : [];
      if (key === 'lyfisScore') {
        isArray = (isArray as UserLyfisScore[]).map((row) => ({
          ...row,
          action: 'view',
        }));
      }
      return {
        active: false,
        name: key,
        data: isArray,
        totalCount: isArray.length,
      };
    });
    this.updatePaginatedData();
  }

  formatDate(epoch: string | Date | undefined) {
    this.utils.convertEpochToFormattedDate;
    return moment(epoch, 'x').format('DD:MM:YYYY');
  }

  getKeys(obj: PanelData): string[] {
    return obj ? Object.keys(obj) : [];
  }

  trackByName(panel: PanelData): string {
    return panel.name;
  }

  updatePaginatedData() {
    this.paginatedData;
    this.paginatedData = this.panels.map((panel) => {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return {
        ...panel,
        data: panel.data.slice(start, end),
      };
    });
  }

  isArrayOfObjects(value: any): boolean {
    return (
      Array.isArray(value) &&
      value.every((item) => typeof item === 'object' && !Array.isArray(item))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  activeChange($event: boolean) {
    this.currentPage = 1;
    this.updatePaginatedData();
  }

  changePage(page: number) {
    this.currentPage = page;
    this.updatePaginatedData();
  }

  therapyCards!: SessionGridStatsData;

  async searchUser() {
    this.spinner = true;
    this.userProfileService
      .searchUserProfile({ emailOrCode: this.email.trim().toLowerCase() })
      .subscribe({
        next: (value) => {
          this.userProfileResp = value;
          const therapyCard = this.sessionService.mapSessionGridStatsResp(
            value.userCardDetails.therapyCards,
          );
          this.therapyCards = therapyCard;
          this.userProfileResp.userCardDetails.userInterestsWithColor = (
            value.userCardDetails.userInterests ?? []
          ).map((data) => {
            return { data: data, color: this.utils.getRandomColor() };
          });
          this.userProfileResp.userCardDetails.userLifestyleChoicesWithColor = (
            value.userCardDetails.userLifestyleChoices ?? []
          ).map((data) => {
            return { data: data, color: this.utils.getRandomColor() };
          });
          this.userProfileResp.userCardDetails.circlesJoinedWithColor = (
            value.userCardDetails.circlesJoined ?? []
          ).map((data) => {
            return { data: data, color: this.utils.getRandomColor() };
          });
          this.generatePanels(value.userTableDetails);
          this.toastrService.showSuccess('Successfully Searched');
          this.spinner = false;
          this.searched = true;
          this.loadPersonalRules();
          this.loadCompanyRules();
          this.loadGlobalRules();
          this.loadExpertChats();
        },
        error: (err) => {
          this.spinner = false;
          this.searched = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
        },
      });
  }

  visible = false;
  selectedNestedKeys: string[] = [];
  selectedRowKeys: any[] = [];
  title: string = '';

  open(nestedKeys: string[], selectedRowKeys: string[], title: string): void {
    this.selectedNestedKeys = nestedKeys;
    this.selectedRowKeys = selectedRowKeys;
    this.title = title;
    this.visible = true;
  }

  close(): void {
    this.visible = false;
  }

  callToUser() {
    this.userProfileService
      .callToUser(this.userProfileResp.userCardDetails.id)
      .subscribe({
        next: () => {
          this.toastrService.showSuccess(
            'Call initiated successfully to user , check your phone',
          );
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
        },
      });
  }

  getWhatsappTemplates() {
    this.loading = true;
    this.userProfileService.listGallaboxWhatsappTemplate().subscribe({
      next: (value) => {
        this.whatsappTemplates = value.map((data) => {
          return {
            data: this.utils.convertToTitleCase(data.name),
            value: data.name,
          };
        });
        this.whatsappGallaboxTemplates = value;
        this.loading = false;
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(errorBody.description);
        this.loading = false;
      },
    });
  }

  blockOrUnClockModalOpen() {
    if (
      this.userProfileResp.userCardDetails.userTags.includes(
        this.userTags.SESSION_BOOKING_BLOCKED,
      )
    ) {
      this.isSessionBookingUnBlockModalOpen = true;
    } else {
      this.isSessionBookingBlockModalOpen = true;
    }
  }

  loadWhitelistedDomains() {
    // Call the whitelisted-domains API endpoint
    this.api
      .get<{ domains: string[] }>(URL_DICT.whitelistedDomainsUrl)
      .subscribe({
        next: (response) => {
          this.whitelistedDomains = response.domains;
        },
        error: (error) => {
          console.error('Failed to load whitelisted domains', error);
        },
      });
  }

  shouldShowFixMemberPassButton(): boolean {
    // Show button for all users for now (simplified logic)
    return !!(this.userProfileResp?.userCardDetails && this.email);
  }

  getPartnerDisplay(): string {
    const partner = this.userProfileResp?.userCardDetails?.userPartner;
    if (partner && partner !== 'N/A') {
      return partner;
    }
    const emailSource = this.email;
    if (!emailSource) {
      return 'N/A';
    }
    const parts = emailSource.split('@');
    return parts.length > 1 ? parts[1] : 'N/A';
  }

  fixMemberPassModalOpen() {
    this.isFixMemberPassModalOpen = true;
  }

  fixMemberPassRedemption() {
    this.isFixingMemberPass = true;
    this.userProfileService
      .fixMemberPassRedemption(this.userProfileResp.userCardDetails.id)
      .subscribe({
        next: (response) => {
          this.isFixingMemberPass = false;
          this.isFixMemberPassModalOpen = false;
          this.toastrService.showSuccess(
            response.message + ' - Corporate Code: ' + response.corporateCode,
          );
          // Refresh user profile to show updated data
          this.searchUser();
        },
        error: (error: IAnyoError) => {
          this.isFixingMemberPass = false;
          this.toastrService.showError(
            error.description || 'Failed to fix member pass redemption',
          );
        },
      });
  }

  async sessionBookingBlock() {
    if (!this.blockUserTillDate) return;

    const blockTillEpoch = moment(this.blockUserTillDate).valueOf();

    this.spinner = true;
    this.userProfileService
      .blockUserSessionBooking(
        this.userProfileResp.userCardDetails.id,
        blockTillEpoch.toString(),
      )
      .subscribe({
        next: async () => {
          this.toastrService.showSuccess('Session Booking Blocked');
          await this.searchUser();
          this.isSessionBookingBlockModalOpen = false;
          this.blockUserTillDate = null;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.spinner = false;
        },
      });
  }

  async sessionBookingUnBlock() {
    this.spinner = true;
    this.userProfileService
      .unblockUserSessionBooking(this.userProfileResp.userCardDetails.id)
      .subscribe({
        next: async () => {
          this.toastrService.showSuccess('Session Booking Unblocked');
          await this.searchUser();
          this.isSessionBookingUnBlockModalOpen = false;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.spinner = false;
        },
      });
  }

  disablePastDates = (current: Date): boolean => {
    return moment(current).isBefore(moment().startOf('day'));
  };

  ngOnInit() {
    this.loadWhitelistedDomains();
    this.masterdataService.masterDataList().subscribe({
      next: (data) => {
        this.verticals = data.therapyVerticals ?? [];
        this.allConcerns = data.therapyConcerns ?? [];
        this.expertLevels = data.therapistExpertLevel ?? [];
      },
    });
    this.therapistService.therapistList().subscribe({
      next: (data) => {
        this.therapists = data;
      },
      // Without this, a failed fetch leaves `therapists` empty silently — every
      // Expert search then shows "No Data" indistinguishably from a real empty result.
      error: () => {
        this.toastrService.showAnyoErrorToast(
          'Could not load the expert list — Expert search will show no results until this succeeds.',
        );
      },
    });
    this.auth.currentAuthStatus.subscribe((user) => {
      if (user) {
        this.activatedRoute.queryParams.subscribe((value) => {
          if (value['code']) {
            this.email = value['code'];
            this.searchUser();
          }
        });
      }
    });
  }

  onDebugVerticalChange() {
    this.debugConcernId = '';
    this.filteredConcerns = this.debugVerticalId
      ? this.allConcerns.filter((c) =>
          c.verticalId.includes(this.debugVerticalId),
        )
      : [];
  }

  therapistDisplayName(t: IAnyoTherapist): string {
    return `${t.title} ${t.firstName} ${t.lastName}`.trim();
  }

  therapistFilterFn = (
    input: string,
    option: { nzLabel: string | number | null },
  ): boolean => {
    return String(option.nzLabel ?? '')
      .toLowerCase()
      .includes(input.toLowerCase());
  };

  evaluateUserPolicy() {
    if (!this.debugVerticalId || !this.debugConcernId) {
      this.toastrService.showAnyoErrorToast('Select a vertical and concern');
      return;
    }
    this.debugLoading = true;
    this.userProfileService
      .debugUserPolicy(
        this.email.trim(),
        this.debugVerticalId,
        this.debugConcernId,
        this.debugTherapistId || undefined,
      )
      .subscribe({
        next: (result) => {
          this.debugResult = result;
          this.debugLoading = false;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.debugLoading = false;
        },
      });
  }

  openPolicyTrace(session: UserSessions) {
    if (!session.policyTrace) return;
    this.selectedSessionTrace = session.policyTrace;
    this.policyTraceDrawerVisible = true;
  }

  closePolicyTrace() {
    this.policyTraceDrawerVisible = false;
    this.selectedSessionTrace = null;
  }

  policyTraceMatchedRuleIds(): Set<string> {
    return new Set(this.selectedSessionTrace?.matchedRuleIds ?? []);
  }

  objectEntries(obj: Record<string, unknown>): [string, unknown][] {
    return Object.entries(obj);
  }

  debugMatchedCount(): number {
    return this.debugResult?.rules.filter((r) => r.matched).length ?? 0;
  }

  resolveRuleVerticals(scope: unknown): string[] {
    const ids = (scope as Record<string, unknown>)?.['verticalId'];
    if (!Array.isArray(ids) || !ids.length) return [];
    return ids.map(
      (id: string) => this.verticals.find((v) => v.m_id === id)?.data ?? id,
    );
  }

  resolveRuleConcerns(scope: unknown): string[] {
    const ids = (scope as Record<string, unknown>)?.['concernId'];
    if (!Array.isArray(ids) || !ids.length) return [];
    return ids.map(
      (id: string) => this.allConcerns.find((c) => c.m_id === id)?.data ?? id,
    );
  }

  getConditionRows(
    conditions: unknown,
  ): { fact: string; operator: string; value: unknown; params?: unknown }[] {
    if (!conditions || typeof conditions !== 'object') return [];
    const obj = conditions as Record<string, unknown>;
    const rows = (obj['all'] as unknown[]) ?? (obj['any'] as unknown[]) ?? [];
    return rows.map((r) => {
      const row = r as Record<string, unknown>;
      return {
        fact: String(row['fact'] ?? ''),
        operator: String(row['operator'] ?? ''),
        value: row['value'],
        ...(row['params'] ? { params: row['params'] } : {}),
      };
    });
  }

  getConditionLogic(conditions: unknown): string {
    if (!conditions || typeof conditions !== 'object') return 'AND';
    const obj = conditions as Record<string, unknown>;
    return obj['any'] ? 'OR' : 'AND';
  }

  condOpLabel(operator: string): string {
    const map: Record<string, string> = {
      equal: '=',
      notEqual: '≠',
      lessThan: '<',
      greaterThan: '>',
      lessThanInclusive: '≤',
      greaterThanInclusive: '≥',
    };
    return map[operator] ?? operator;
  }

  displayName: string = '';
  phoneNumber: string = '';
  visiblePh: boolean = false;

  openDrawer(): void {
    this.displayName = this.userProfileResp.userCardDetails.name;
    this.phoneNumber = this.userProfileResp.userCardDetails.phoneNumber;
    this.visiblePh = true;
  }

  closeDrawer(): void {
    this.visiblePh = false;
  }
  updateLoading = false;
  retryingLabReports = false;
  updateUserProfile() {
    if (this.displayName.trim().length === 0) {
      this.toastrService.showAnyoErrorToast('Display Name cannot be empty');
      return;
    }
    if (this.phoneNumber.trim().length === 0) {
      this.toastrService.showAnyoErrorToast('Phone Number cannot be empty');
      return;
    }
    this.updateLoading = true;
    this.userProfileService
      .updateUserProfile(
        this.userProfileResp.userCardDetails.id,
        this.displayName,
        this.phoneNumber,
      )
      .subscribe({
        next: async () => {
          this.toastrService.showSuccess('User Profile Updated');
          await this.searchUser();
          this.visiblePh = false;
          this.updateLoading = false;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.spinner = false;
          this.updateLoading = false;
        },
      });
  }

  newEmail: string = '';
  visibleEmailModal: boolean = false;
  updateEmailLoading = false;

  openEmailModal(): void {
    this.newEmail = '';
    this.visibleEmailModal = true;
  }

  closeEmailModal(): void {
    this.visibleEmailModal = false;
  }

  updateUserEmail(): void {
    if (!this.utils.validateEmail(this.newEmail)) {
      this.toastrService.showAnyoErrorToast('Enter a valid email address');
      return;
    }
    this.updateEmailLoading = true;
    this.userProfileService
      .updateUserEmail(this.userProfileResp.userCardDetails.id, this.newEmail)
      .subscribe({
        next: () => {
          this.toastrService.showSuccess('User email updated');
          this.userProfileResp.userCardDetails.email = this.newEmail
            .trim()
            .toLowerCase();
          this.visibleEmailModal = false;
          this.updateEmailLoading = false;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.updateEmailLoading = false;
        },
      });
  }

  emailAuditLog: EmailAuditLogEntry[] = [];
  visibleEmailAuditModal: boolean = false;
  loadingEmailAuditLog = false;

  openEmailAuditLogModal(): void {
    this.visibleEmailAuditModal = true;
    this.loadingEmailAuditLog = true;
    this.userProfileService
      .getUserEmailAuditLog(this.userProfileResp.userCardDetails.id)
      .subscribe({
        next: (log) => {
          this.emailAuditLog = log;
          this.loadingEmailAuditLog = false;
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.loadingEmailAuditLog = false;
        },
      });
  }

  closeEmailAuditLogModal(): void {
    this.visibleEmailAuditModal = false;
  }

  formatAuditDate(isoDate: string): string {
    return moment(isoDate).format('DD/MM/YYYY hh:mm A');
  }

  retryUserLabReports(): void {
    const userId = this.userProfileResp?.userCardDetails?.id;

    if (!userId) {
      this.toastrService.showAnyoErrorToast('User ID not available');
      return;
    }

    this.retryingLabReports = true;

    this.userProfileService.retryUserLabReportsProcessing(userId).subscribe({
      next: (response: any) => {
        this.retryingLabReports = false;
        if (response.data.totalReports === 0) {
          this.toastrService.showInfo(
            'No stuck lab reports found for this user',
          );
        } else {
          this.toastrService.showSuccess(
            `Successfully retried ${response.data.retriedReports} out of ${response.data.totalReports} stuck reports`,
          );
        }
      },
      error: (error) => {
        this.retryingLabReports = false;
        const errorBody = error.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description || 'Failed to retry lab reports',
        );
      },
    });
  }
  handleAction(fieldName: string) {
    switch (fieldName) {
      case 'lyfisScore':
        this.openLyfisUser();
        break;
      default:
        break;
    }
  }

  openLyfisUser() {
    this.router.navigate([
      '/lyfis/view-scores/users',
      this.userProfileResp?.userCardDetails?.id,
      'snapshots',
    ]);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Personal Policy Rules (APH-5224) — per-user rule override, reusing the
  // FOR/WHEN/THEN card pattern already duplicated in policy-controls and
  // global-policy-rules. Target user is implicit (this searched user), not
  // a company picker — companyId comes from userCardDetails.companyId.
  // ═══════════════════════════════════════════════════════════════════════

  loadPersonalRules(): void {
    const userId = this.userProfileResp?.userCardDetails?.id;
    if (!userId) return;
    this.personalRulesLoading = true;
    this.policyService.getUserPolicyRules(userId).subscribe({
      next: (rules) => {
        this.personalRulesUI = rules.map(toPersonalRuleUI);
        this.personalRulesLoading = false;
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Failed to load personal rules');
        this.personalRulesLoading = false;
      },
    });
  }

  /** Read-only: this employee's current company's rules, excluding personal overrides
   * (those are shown separately above) — same list-fetching call as policy-controls.component,
   * filtered the same way it filters its own companyRulesUI getter. */
  loadCompanyRules(): void {
    const companyId = this.userProfileResp?.userCardDetails?.companyId;
    if (!companyId) {
      this.companyRulesUI = [];
      return;
    }
    this.companyRulesLoading = true;
    this.policyService.getPolicyRules(companyId).subscribe({
      next: (rules) => {
        this.companyRulesUI = rules
          .filter((r) => !r.isGlobal && !r.userId)
          .map(toPersonalRuleUI);
        this.companyRulesLoading = false;
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Failed to load company rules');
        this.companyRulesLoading = false;
      },
    });
  }

  /** Read-only: platform-wide global rules — same list-fetching call as
   * global-policy-rules.component. */
  loadGlobalRules(): void {
    this.globalRulesLoading = true;
    this.policyService.getGlobalPolicyRules().subscribe({
      next: (rules) => {
        this.globalRulesUI = rules.map(toPersonalRuleUI);
        this.globalRulesLoading = false;
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Failed to load global rules');
        this.globalRulesLoading = false;
      },
    });
  }

  addPersonalRule(): void {
    if (!this.canAddPersonalRule) {
      this.toastrService.showAnyoErrorToast(
        'Company ID not available for this user — cannot create a personal rule yet',
      );
      return;
    }
    this.personalRulesUI.unshift({
      name: '',
      priority: 10,
      active: true,
      action: 'Session Booking',
      scope: {},
      conditionMode: 'always',
      condLogic: 'all',
      condRows: [],
      outputs: {},
      expanded: true,
      saving: false,
    });
  }

  togglePersonalRuleExpand(rule: PersonalRuleUI): void {
    rule.expanded = !rule.expanded;
  }

  onPersonalRuleActionChange(rule: PersonalRuleUI): void {
    rule.condRows = [];
    rule.outputs = {};
  }

  addPersonalCond(rule: PersonalRuleUI): void {
    rule.condRows.push({ fact: '', operator: 'equal', value: '' });
  }

  removePersonalCond(rule: PersonalRuleUI, idx: number): void {
    rule.condRows.splice(idx, 1);
  }

  onPersonalFactChange(rule: PersonalRuleUI, row: PersonalConditionRow): void {
    const meta = (PERSONAL_RULE_FACTS[rule.action] ?? []).find((f) => f.value === row.fact);
    row.operator = 'equal';
    row.value = meta?.type === 'bool' ? false : '';
    row.params = meta?.hasParams ? { days: 7 } : undefined;
  }

  personalFactType(action: string, fact: string): 'number' | 'bool' {
    return (PERSONAL_RULE_FACTS[action] ?? []).find((f) => f.value === fact)?.type ?? 'number';
  }

  personalFactsFor(action: string): { value: string; label: string; type: 'number' | 'bool'; hasParams?: boolean }[] {
    return PERSONAL_RULE_FACTS[action] ?? [];
  }

  personalOutputsFor(action: string) {
    return PERSONAL_RULE_OUTPUTS[action] ?? [];
  }

  /** True when this output should be shown at all — self-pay pricing fields are hidden
   * (not just disabled) when they don't apply, matching the company/global rule forms. */
  isPersonalFieldVisible(rule: PersonalRuleUI, out: { key: string }): boolean {
    if (!PERSONAL_SELF_PAY_PRICING_FIELDS.includes(out.key)) return true;
    if (rule.outputs['payer'] !== 'user') return false;
    if (out.key === 'selfPayPricingMode') return true;
    const mode = rule.outputs['selfPayPricingMode'];
    if (PERSONAL_SELF_PAY_SLAB_ONLY_FIELDS.includes(out.key)) return mode === 'slab';
    if (PERSONAL_SELF_PAY_FLAT_ONLY_FIELDS.includes(out.key)) return mode === 'flat';
    return true;
  }

  /** Clears self-pay pricing fields that just became irrelevant. */
  onPersonalSelfPayOutputChange(rule: PersonalRuleUI, changedKey: string): void {
    if (changedKey === 'payer' && rule.outputs['payer'] !== 'user') {
      for (const key of PERSONAL_SELF_PAY_PRICING_FIELDS) rule.outputs[key] = '';
      rule.outputs['selfPayFlatSlabPricing'] = [];
      return;
    }
    if (changedKey === 'selfPayPricingMode') {
      const mode = rule.outputs['selfPayPricingMode'];
      if (mode !== 'slab') for (const key of PERSONAL_SELF_PAY_SLAB_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flat') for (const key of PERSONAL_SELF_PAY_FLAT_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flatSlab') rule.outputs['selfPayFlatSlabPricing'] = [];
    }
  }

  /** True when the flatSlab price-by-duration row editor should be shown for this rule. */
  showPersonalFlatSlabEditor(rule: PersonalRuleUI): boolean {
    return rule.outputs['payer'] === 'user' && rule.outputs['selfPayPricingMode'] === 'flatSlab';
  }

  personalFlatSlabRows(rule: PersonalRuleUI): { minDuration: number | null; maxDuration: number | null; amount: number | null }[] {
    return (rule.outputs['selfPayFlatSlabPricing'] as { minDuration: number | null; maxDuration: number | null; amount: number | null }[]) ?? [];
  }

  addPersonalFlatSlabRow(rule: PersonalRuleUI): void {
    if (!Array.isArray(rule.outputs['selfPayFlatSlabPricing'])) {
      rule.outputs['selfPayFlatSlabPricing'] = [];
    }
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[]).push({
      minDuration: null,
      maxDuration: null,
      amount: null,
    });
  }

  removePersonalFlatSlabRow(rule: PersonalRuleUI, idx: number): void {
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[])?.splice(idx, 1);
  }

  isPersonalActionBlocked(rule: PersonalRuleUI): boolean {
    const field = PERSONAL_ACTION_ALLOWED_FIELD[rule.action];
    if (!field) return false;
    return rule.outputs[field] === 'false' || rule.outputs[field] === false;
  }

  isPersonalOutputDisabled(rule: PersonalRuleUI, out: { key: string }): boolean {
    if (!this.isPersonalActionBlocked(rule)) return false;
    return (PERSONAL_BLOCKED_CLEARS[rule.action] ?? []).includes(out.key);
  }

  onPersonalAllowedFieldChange(rule: PersonalRuleUI): void {
    if (!this.isPersonalActionBlocked(rule)) return;
    for (const key of PERSONAL_BLOCKED_CLEARS[rule.action] ?? []) {
      rule.outputs[key] = '';
    }
  }

  personalConcernsForRule(rule: PersonalRuleUI): TherapyConcerns[] {
    if (!rule.scope?.verticalId?.length) return this.allConcerns;
    const verticalSet = new Set(rule.scope.verticalId);
    return this.allConcerns.filter((c) => c.verticalId?.some((vid) => verticalSet.has(vid)));
  }

  personalScopeSummary(rule: PersonalRuleUI): string {
    const parts = [
      rule.scope.verticalId?.length
        ? rule.scope.verticalId.map((id) => this.verticals.find((v) => v.m_id === id)?.data ?? id).join(', ')
        : null,
      rule.scope.concernId?.length
        ? rule.scope.concernId.map((id) => this.allConcerns.find((c) => c.m_id === id)?.data ?? id).join(', ')
        : null,
      rule.scope.expertLevelId
        ? (this.expertLevels.find((e) => e.m_id === rule.scope.expertLevelId)?.data ?? rule.scope.expertLevelId)
        : null,
      rule.scope.therapistIds?.length
        ? rule.scope.therapistIds.map((id) => this.therapistNameById(id)).join(', ')
        : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'All';
  }

  personalCondSummary(rule: PersonalRuleUI): string {
    if (rule.conditionMode === 'always' || !rule.condRows.length) return 'Always applies';
    const opMap: Record<string, string> = {
      equal: '=', notEqual: '≠', lessThan: '<', greaterThan: '>',
      lessThanInclusive: '≤', greaterThanInclusive: '≥',
    };
    const sep = rule.condLogic === 'any' ? ' OR ' : ' AND ';
    const parts = rule.condRows.slice(0, 2).map((c) => {
      if (!c.fact) return '?';
      if (this.personalFactType(rule.action, c.fact) === 'bool') return `${c.fact}=${c.value ? 'yes' : 'no'}`;
      return `${c.fact} ${opMap[c.operator] ?? c.operator} ${c.value}`;
    });
    return 'When ' + parts.join(sep) + (rule.condRows.length > 2 ? ' …' : '');
  }

  personalThenSummary(rule: PersonalRuleUI): string {
    const outs = PERSONAL_RULE_OUTPUTS[rule.action] ?? [];
    const set = outs.filter(
      (o) => rule.outputs[o.key] !== '' && rule.outputs[o.key] !== undefined && rule.outputs[o.key] !== null,
    );
    if (!set.length) return '(no outputs)';
    const short: Record<string, string> = {
      payer: 'payer', sessionMinutes: 'duration', sessionAllowed: 'allowed',
      eventAllowed: 'allowed', diagAllowed: 'allowed', cooldown: 'cooldown', blockReason: 'reason',
    };
    return (
      set.slice(0, 3).map((o) => {
        const label = short[o.key] ?? o.key;
        const val = o.key === 'blockReason' ? '✓' : rule.outputs[o.key];
        return `${label}=${val}`;
      }).join(' · ') + (set.length > 3 ? ` +${set.length - 3}` : '')
    );
  }

  /** Resolves a therapistId to a display name; falls back to a "(not found)" marker for a
   * deleted/deactivated therapist rather than throwing on a missing lookup. */
  therapistNameById(therapistId: string | undefined): string {
    if (!therapistId) return '';
    const t = this.therapists.find((x) => x._id === therapistId);
    return t ? this.therapistDisplayName(t) : `${therapistId} (not found)`;
  }

  savePersonalRule(rule: PersonalRuleUI): void {
    const userId = this.userProfileResp?.userCardDetails?.id;
    const companyId = this.userProfileResp?.userCardDetails?.companyId;
    if (!userId || !companyId) {
      this.toastrService.showAnyoErrorToast(
        'Company ID not available for this user — cannot save a personal rule yet',
      );
      return;
    }
    if (!rule.name.trim()) {
      this.toastrService.showAnyoErrorToast('Rule name is required');
      return;
    }
    if (this.isPersonalActionBlocked(rule)) {
      const blocked = PERSONAL_BLOCKED_CLEARS[rule.action] ?? [];
      const hasConflict = blocked.some(
        (k) => rule.outputs[k] !== '' && rule.outputs[k] != null,
      );
      if (hasConflict) {
        this.toastrService.showAnyoErrorToast(
          'Cannot set payer or session options when booking is blocked — clear those fields first.',
        );
        return;
      }
    }
    rule.saving = true;
    const payload = fromPersonalRuleUI(rule, userId, companyId);
    const call = rule.id
      ? this.policyService.updateUserPolicyRule(rule.id, payload)
      : this.policyService.createUserPolicyRule(payload);
    call.subscribe({
      next: () => {
        this.toastrService.showSuccess('Personal rule saved');
        rule.saving = false;
        rule.expanded = false;
        this.loadPersonalRules();
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description || 'Failed to save personal rule',
        );
        rule.saving = false;
      },
    });
  }

  deletePersonalRule(rule: PersonalRuleUI): void {
    if (!rule.id) {
      this.personalRulesUI = this.personalRulesUI.filter((r) => r !== rule);
      return;
    }
    this.policyService.deleteUserPolicyRule(rule.id).subscribe({
      next: () => {
        this.toastrService.showSuccess('Personal rule deleted');
        this.personalRulesUI = this.personalRulesUI.filter((r) => r !== rule);
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Failed to delete personal rule');
      },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // DM Control table (APH-5224) — one row per (userId, therapistId) pair with
  // an existing chat session (expert-to-expert chats excluded server-side).
  // Reactive-only: a row only exists after at least one message was sent.
  // ═══════════════════════════════════════════════════════════════════════

  loadExpertChats(): void {
    const userId = this.userProfileResp?.userCardDetails?.id;
    if (!userId) return;
    this.expertChatsLoading = true;
    this.userProfileService.getExpertChats(userId).subscribe({
      next: (rows) => {
        this.expertChats = rows.map((row) => ({ ...row, saving: false }));
        this.expertChatsLoading = false;
      },
      error: () => {
        this.toastrService.showAnyoErrorToast('Failed to load expert chats');
        this.expertChatsLoading = false;
      },
    });
  }

  /** undefined/true = enabled per contract — never bind the raw possibly-undefined
   * `dmEnabled` value directly to a switch, or an unset row renders as OFF. */
  isDmEnabled(row: ExpertChatRowUI): boolean {
    return row.dmEnabled !== false;
  }

  toggleDm(row: ExpertChatRowUI, enabled: boolean): void {
    const userId = this.userProfileResp?.userCardDetails?.id;
    if (!userId || row.saving) return; // guards rapid double-click, not just visually
    const previous = row.dmEnabled;
    row.saving = true;
    row.dmEnabled = enabled; // optimistic
    this.userProfileService.toggleExpertChatDm(userId, row.therapistId, enabled).subscribe({
      next: () => {
        row.saving = false;
        this.toastrService.showSuccess(`DM ${enabled ? 'enabled' : 'disabled'} for ${row.therapistName}`);
      },
      error: () => {
        row.dmEnabled = previous; // revert optimistic update
        row.saving = false;
        this.toastrService.showAnyoErrorToast('Failed to update DM setting');
      },
    });
  }

  formatExpertChatTime(value: string | number | undefined): string {
    if (!value) return '—';
    return moment(value).format('DD/MM/YYYY hh:mm A');
  }
}
